import { useEffect, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import {
  STATUS_LABELS,
  TIPO_LABELS,
  SOLUCOES_LABELS,
  REGIAO_LABELS,
  CONFIANCA_LABELS,
  FONTE_TIPO_LABELS,
  PAPEL_FONTE_LABELS,
  PAPEL_RADAR_LABELS,
  MOTIVO_DESCARTE_LABELS,
  formatBRL,
  formatDate,
  type FonteVinculo,
  type RadarOportunidade,
} from '@/pages/painel/lib/radarConstants';
import RadarScore from './RadarScore';
import RadarOportunidadeFormModal from './RadarOportunidadeFormModal';

interface Relacionada {
  id: number;
  papel: string;
  observacao: string | null;
  proxima_acao: string | null;
  proxima_acao_em: string | null;
  empresa_id: number | null;
  empresa: { nome: string; cidade: string | null; uf: string | null; segmento: string | null } | null;
}

interface EmpresaOption {
  id: number;
  nome: string;
  cidade: string | null;
  uf: string | null;
  segmento: string | null;
}

export default function RadarOportunidadeDetail() {
  const { password } = useOutletContext<{ password: string }>();
  const { id } = useParams<{ id: string }>();

  const [op, setOp] = useState<RadarOportunidade | null>(null);
  const [relacionadas, setRelacionadas] = useState<Relacionada[]>([]);
  const [fontes, setFontes] = useState<FonteVinculo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [convertendo, setConvertendo] = useState(false);
  const [convertido, setConvertido] = useState<{ empresa_id: number; crm_oportunidade_id: number } | null>(null);

  // associação de empresa
  const [assocOpen, setAssocOpen] = useState(false);
  const [empresas, setEmpresas] = useState<EmpresaOption[]>([]);
  const [empSelected, setEmpSelected] = useState<number | ''>('');
  const [papel, setPapel] = useState('empresa_alvo');
  const [obs, setObs] = useState('');
  const [acao, setAcao] = useState('');
  const [acaoEm, setAcaoEm] = useState('');
  const [assocLoading, setAssocLoading] = useState(false);
  const [assocError, setAssocError] = useState<string | null>(null);

  const [descarteMotivo, setDescarteMotivo] = useState('outro');
  const [descarteOpen, setDescarteOpen] = useState(false);

  const oportunidadeId = Number(id);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('radar-admin', {
        body: { action: 'detalhar', password, oportunidade_id: oportunidadeId },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { oportunidade?: RadarOportunidade; relacionadas?: Relacionada[]; fontes?: FonteVinculo[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setOp(parsed?.oportunidade ?? null);
      setRelacionadas(parsed?.relacionadas ?? []);
      setFontes(parsed?.fontes ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar a oportunidade.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (oportunidadeId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oportunidadeId, password]);

  const abrirAssociar = async () => {
    setAssocOpen(true);
    setEmpSelected('');
    setPapel('empresa_alvo');
    setObs('');
    setAcao('');
    setAcaoEm('');
    setAssocError(null);
    setAssocLoading(true);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('radar-admin', {
        body: { action: 'listar_empresas', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { empresas?: EmpresaOption[]; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setEmpresas(parsed?.empresas ?? []);
    } catch (e) {
      setAssocError(e instanceof Error ? e.message : 'Erro ao listar empresas.');
    } finally {
      setAssocLoading(false);
    }
  };

  const confirmarAssociar = async () => {
    if (!empSelected) return;
    setAssocLoading(true);
    setAssocError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-relacionamentos', {
        body: {
          action: 'relacionar_radar',
          password,
          empresa_id: Number(empSelected),
          radar_oportunidade_id: oportunidadeId,
          papel,
          observacao: obs,
          proxima_acao: acao,
          proxima_acao_em: acaoEm,
        },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setAssocOpen(false);
      await load();
    } catch (e) {
      setAssocError(e instanceof Error ? e.message : 'Erro ao associar empresa.');
    } finally {
      setAssocLoading(false);
    }
  };

  const desvincular = async (vinculoId: number) => {
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('crm-relacionamentos', {
        body: { action: 'desrelacionar_radar', password, vinculo_id: vinculoId },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao desvincular empresa.');
    }
  };

  const converterCrm = async () => {
    setConvertendo(true);
    setError(null);
    setConvertido(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('radar-admin', {
        body: { action: 'converter_crm', password, oportunidade_id: oportunidadeId },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { empresa_id?: number; crm_oportunidade_id?: number; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setConvertido({ empresa_id: parsed?.empresa_id ?? 0, crm_oportunidade_id: parsed?.crm_oportunidade_id ?? 0 });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao converter para o CRM.');
    } finally {
      setConvertendo(false);
    }
  };

  const descartar = async () => {
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('radar-admin', {
        body: { action: 'atualizar_status', password, oportunidade_id: oportunidadeId, status: 'descartada', motivo_descarte: descarteMotivo },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setDescarteOpen(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao descartar.');
    }
  };

  if (loading && !op) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !op) {
    return (
      <div className="space-y-4">
        <Link to="/painel/radar/oportunidades" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap">
          <i className="ri-arrow-left-line"></i>
          Voltar para oportunidades
        </Link>
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }

  if (!op) return null;

  return (
    <div className="space-y-5">
      <Link to="/painel/radar/oportunidades" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap">
        <i className="ri-arrow-left-line"></i>
        Voltar para oportunidades
      </Link>

      {convertido && (
        <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
          <i className="ri-check-double-line mr-1.5"></i>
          Convertida para o CRM com sucesso. Veja a empresa na{' '}
          <Link to={`/painel/clientes/${convertido.empresa_id}`} className="font-semibold underline">Conta 360º</Link>.
        </div>
      )}

      {/* Cabeçalho */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 text-[#E8610A] rounded-2xl flex-shrink-0">
              <i className="ri-radar-line text-xl"></i>
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-bold text-gray-900">{op.titulo}</h2>
              {op.empresa && <p className="text-sm text-gray-500">{op.empresa}</p>}
              <div className="flex items-center gap-1.5 flex-wrap mt-2">
                {op.tipo && (
                  <span className="text-xs font-semibold text-gray-600 bg-gray-100 rounded-full px-2.5 py-1">
                    {TIPO_LABELS[op.tipo] ?? op.tipo}
                  </span>
                )}
                <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${
                  op.motor === 'grandes_projetos' ? 'bg-purple-50 text-purple-700' : 'bg-green-50 text-green-700'
                }`}>
                  {op.motor === 'grandes_projetos' ? 'Motor Grandes Projetos' : 'Motor Caixa'}
                </span>
                <span className="text-xs font-semibold text-[#E8610A] bg-[#E8610A]/10 rounded-full px-2.5 py-1">
                  {STATUS_LABELS[op.status] ?? op.status}
                </span>
                {op.status === 'convertida_crm' && op.crm_oportunidade_id && (
                  <Link
                    to={`/painel/clientes/${op.empresa_id ?? ''}`}
                    className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1"
                  >
                    <i className="ri-check-double-line mr-1"></i>Convertida para CRM
                  </Link>
                )}
              </div>
            </div>
          </div>
          <RadarScore op={op} />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Valor potencial HOP</p>
          <p className="text-lg font-bold text-gray-900">
            {op.valor_potencial_hop != null ? formatBRL(op.valor_potencial_hop) : <span className="text-sm text-gray-400">Não estimado</span>}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Lucro potencial</p>
          <p className="text-lg font-bold text-green-600">{formatBRL(op.lucro_potencial)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Margem estimada</p>
          <p className="text-lg font-bold text-gray-900">{op.margem_estimada != null ? `${op.margem_estimada}%` : '—'}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Empresas relacionadas</p>
          <p className="text-lg font-bold text-gray-900">{relacionadas.length}</p>
        </div>
      </div>

      {/* Ações */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setEditOpen(true)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] px-4 py-2.5 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-edit-line"></i>
          Editar
        </button>
        {op.status !== 'convertida_crm' && (
          <button
            onClick={converterCrm}
            disabled={convertendo}
            className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 px-4 py-2.5 rounded-xl cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
          >
            <i className={convertendo ? 'ri-loader-4-line animate-spin' : 'ri-exchange-line'}></i>
            {convertendo ? 'Convertendo...' : 'Converter em oportunidade comercial'}
          </button>
        )}
        {op.status !== 'descartada' && (
          <button
            onClick={() => setDescarteOpen(true)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-4 py-2.5 rounded-xl cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-archive-line"></i>
            Descartar
          </button>
        )}
      </div>

      {/* Dados */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="text-sm font-bold text-gray-900 mb-4">Dados da oportunidade</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: 'Segmento', value: op.segmento },
                { label: 'Cidade / UF', value: [op.cidade, op.uf].filter(Boolean).join(' - ') },
                { label: 'Região', value: op.regiao ? REGIAO_LABELS[op.regiao] ?? op.regiao : '' },
                { label: 'Confiança', value: op.confianca ? CONFIANCA_LABELS[op.confianca] ?? op.confianca : '' },
                { label: 'Descoberto em', value: formatDate(op.data_descoberta ?? op.created_at) },
                { label: 'Estágio do projeto', value: op.estagio_projeto },
                { label: 'Previsão de compra', value: formatDate(op.previsao_compra) },
                { label: 'Responsável', value: op.responsavel },
                { label: 'Valor da obra', value: op.valor_obra != null ? formatBRL(op.valor_obra) : 'Não estimado' },
              ].map((c) => (
                <div key={c.label}>
                  <p className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold">{c.label}</p>
                  <p className="text-sm text-gray-800 mt-0.5 break-words">{c.value || '—'}</p>
                </div>
              ))}
            </div>

            {op.obra_id && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold mb-1">Obra vinculada</p>
                <Link to="/painel/radar/obras" className="text-sm text-[#E8610A] hover:underline">
                  <i className="ri-building-4-line mr-1"></i>Ver obra de origem
                </Link>
              </div>
            )}

            {op.procedencia && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold mb-2">Dados importados da obra</p>
                {op.valor_contrato_cau != null && (
                  <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 mb-3">
                    <i className="ri-information-line text-amber-600 mt-0.5"></i>
                    <p className="text-xs text-amber-800">
                      Valor de contrato técnico (RRT/CAU): <strong>{formatBRL(op.valor_contrato_cau)}</strong> — não é valor da obra nem potencial comercial.
                    </p>
                  </div>
                )}
                <ul className="space-y-1">
                  {Object.entries(op.procedencia.campos).map(([campo, origem]) => (
                    <li key={campo} className="flex items-start justify-between gap-3 text-xs">
                      <span className="text-gray-500">{campo}</span>
                      <span className="text-gray-700 text-right">{origem}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {fontes.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold mb-1">Fontes</p>
                <div className="space-y-1.5">
                  {fontes.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-gray-800">{f.fonte_nome ?? 'Fonte'}</span>
                      {f.fonte_tipo && (
                        <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                          {FONTE_TIPO_LABELS[f.fonte_tipo] ?? f.fonte_tipo}
                        </span>
                      )}
                      <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 whitespace-nowrap ${
                        f.papel === 'origem_principal' ? 'bg-[#E8610A]/10 text-[#E8610A]' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {PAPEL_FONTE_LABELS[f.papel] ?? f.papel}
                      </span>
                      {f.url_especifica && (
                        <a href={f.url_especifica} target="_blank" rel="noopener noreferrer" className="text-xs text-[#E8610A] hover:underline break-all">
                          {f.url_especifica}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {op.url_fonte && (
              <div className="mt-4">
                <p className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold mb-1">URL da fonte</p>
                <a href={op.url_fonte} target="_blank" rel="noopener noreferrer" className="text-sm text-[#E8610A] hover:underline break-all">
                  {op.url_fonte}
                </a>
              </div>
            )}
            {op.descricao && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold mb-1">Descrição</p>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{op.descricao}</p>
              </div>
            )}
            {op.observacoes && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold mb-1">Observações</p>
                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{op.observacoes}</p>
              </div>
            )}
            {op.motivo_descarte && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-[11px] text-gray-400 uppercase tracking-wider font-semibold mb-1">Motivo do descarte</p>
                <span className="text-xs font-semibold text-gray-600 bg-gray-100 rounded-full px-2.5 py-1">
                  {MOTIVO_DESCARTE_LABELS[op.motivo_descarte] ?? op.motivo_descarte}
                </span>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Soluções HOP possíveis</h3>
            {(op.solucoes ?? []).length === 0 ? (
              <p className="text-sm text-gray-400">Nenhuma solução indicada.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {(op.solucoes ?? []).map((s) => (
                  <span key={s} className="text-xs font-semibold text-[#E8610A] bg-[#E8610A]/10 rounded-full px-2.5 py-1">
                    {SOLUCOES_LABELS[s] ?? s}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Empresas relacionadas */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between gap-2 mb-4">
            <h3 className="text-sm font-bold text-gray-900">Empresas relacionadas</h3>
            <button
              onClick={abrirAssociar}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              Associar empresa
            </button>
          </div>

          {relacionadas.length === 0 ? (
            <p className="text-sm text-gray-400">Nenhuma empresa associada ainda.</p>
          ) : (
            <div className="space-y-2">
              {relacionadas.map((r) => (
                <div key={r.id} className="border border-gray-100 rounded-xl p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {r.empresa ? (
                          <Link to={`/painel/clientes/${r.empresa_id}`} className="text-sm font-semibold text-gray-900 hover:text-[#E8610A] transition-colors">
                            {r.empresa.nome}
                          </Link>
                        ) : (
                          <span className="text-sm font-semibold text-gray-500">Empresa removida</span>
                        )}
                        <span className="text-[11px] font-semibold text-[#E8610A] bg-[#E8610A]/10 rounded-full px-2 py-0.5 whitespace-nowrap">
                          {PAPEL_RADAR_LABELS[r.papel] ?? r.papel}
                        </span>
                      </div>
                      {r.empresa && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {[r.empresa.cidade, r.empresa.uf].filter(Boolean).join(' - ') || ''}
                          {r.empresa.segmento ? ` · ${r.empresa.segmento}` : ''}
                        </p>
                      )}
                      {r.proxima_acao && (
                        <p className="text-xs text-gray-600 mt-1">
                          <span className="text-gray-400">Próxima ação:</span> {r.proxima_acao}
                          {r.proxima_acao_em && <span className="text-gray-400"> · {formatDate(r.proxima_acao_em)}</span>}
                        </p>
                      )}
                      {r.observacao && <p className="text-xs text-gray-500 mt-0.5 italic">{r.observacao}</p>}
                    </div>
                    <button
                      onClick={() => desvincular(r.id)}
                      className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg cursor-pointer shrink-0"
                      title="Remover relação"
                    >
                      <i className="ri-link-unlink text-sm"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {editOpen && (
        <RadarOportunidadeFormModal
          op={op}
          password={password}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load(); }}
        />
      )}

      {/* Modal associar empresa */}
      {assocOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setAssocOpen(false)}>
          <div className="bg-white rounded-2xl border border-gray-200 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-gray-900 text-sm">Associar empresa a esta oportunidade</h3>
              <button onClick={() => setAssocOpen(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              {assocError && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{assocError}</div>}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Empresa</label>
                <select value={empSelected} onChange={(e) => setEmpSelected(e.target.value ? Number(e.target.value) : '')} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]">
                  <option value="">Selecione uma empresa...</option>
                  {empresas.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nome}
                      {e.cidade ? ` — ${e.cidade}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Papel</label>
                <select value={papel} onChange={(e) => setPapel(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]">
                  {Object.entries(PAPEL_RADAR_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Próxima ação (opcional)</label>
                <input type="text" value={acao} onChange={(e) => setAcao(e.target.value)} placeholder="Ex: apresentar portfólio de divisórias" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Data</label>
                <input type="date" value={acaoEm} onChange={(e) => setAcaoEm(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Observação (opcional)</label>
                <input type="text" value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Ex: construtora responsável pela obra" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]" />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setAssocOpen(false)} className="inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer whitespace-nowrap">
                  Cancelar
                </button>
                <button
                  onClick={confirmarAssociar}
                  disabled={assocLoading || !empSelected}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {assocLoading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-link"></i>}
                  Associar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal descartar */}
      {descarteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDescarteOpen(false)}>
          <div className="bg-white rounded-2xl border border-gray-200 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-gray-900 text-sm">Descartar oportunidade</h3>
              <button onClick={() => setDescarteOpen(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Motivo</label>
                <select value={descarteMotivo} onChange={(e) => setDescarteMotivo(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white cursor-pointer focus:outline-none focus:border-[#E8610A]">
                  {Object.entries(MOTIVO_DESCARTE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setDescarteOpen(false)} className="inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer whitespace-nowrap">
                  Cancelar
                </button>
                <button
                  onClick={descartar}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-lg cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-archive-line"></i>
                  Descartar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}